import PushSheet from '@/components/PushSheet';
import { useCustomerSearch } from '@/features/customers/hooks/useCustomers';
import { useT } from '@/i18n';
import { formatPhone } from '@/utils/phone';
import type { TableSession } from '@restaurant/api-client';
import type { Customer } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  Button,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  PageContent,
  Toolbar,
  f7,
} from 'framework7-react';
import { useEffect, useState } from 'react';
import { useLinkCustomer, useUnlinkCustomer } from '../hooks/useTableOrdering';

function primaryPhone(c: Customer): string {
  const p = c.phoneNumbers.find((x) => x.isPrimary) ?? c.phoneNumbers[0];
  return p?.phone ?? '';
}

/**
 * Link a CRM customer to the tab - pick an existing one (typeahead, so the
 * pool grows by reuse, not duplicates) or capture a new guest by name + phone
 * (phone-first find-or-create, server-side). Mirrors AttachReservationSheet's
 * Rule-10 shape: stateless wrapper renders <PushSheet> + <Toolbar>; the form
 * state lives in a *Content child.
 */
export default function AssignCustomerSheet({
  session,
  open,
  onClose,
}: {
  session: TableSession;
  open: boolean;
  onClose: () => void;
}) {
  const tAll = useT();
  const t = tAll.tableOrdering.assignCustomer;
  return (
    <PushSheet open={open} onClose={onClose} style={{ height: '82vh' }}>
      <Toolbar>
        <div className="left">
          <F7Link sheetClose>{tAll.cancel}</F7Link>
        </div>
        <div className="title">{session.customer ? t.change : t.title}</div>
        <div className="right" />
      </Toolbar>
      <AssignCustomerContent session={session} open={open} />
    </PushSheet>
  );
}

function AssignCustomerContent({ session, open }: { session: TableSession; open: boolean }) {
  const tAll = useT();
  const t = tAll.tableOrdering.assignCustomer;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [term, setTerm] = useState('');
  const link = useLinkCustomer();
  const unlink = useUnlinkCustomer();
  const linked = session.customer;

  // Reset the form whenever the sheet opens (it's always mounted - Rule 10).
  useEffect(() => {
    if (open) {
      setName('');
      setPhone('');
      setTerm('');
    }
  }, [open]);

  // Typeahead off the name field - same idiom as Create Reservation.
  useEffect(() => {
    const t = setTimeout(() => setTerm(name.trim()), 250);
    return () => clearTimeout(t);
  }, [name]);

  const { data } = useCustomerSearch(term);
  const suggestions = (data?.items ?? []).filter((c) => c.id !== linked?.id);
  const showSuggestions = term.length >= 2 && suggestions.length > 0;

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const linkExisting = (c: Customer) =>
    link.mutate(
      { sessionId: session.id, body: { customerId: c.id } },
      {
        onSuccess: () => {
          toast(t.linkedName(c.firstName).trim());
          f7.sheet.close();
        },
      },
    );

  const linkNew = () => {
    const n = name.trim();
    const p = phone.trim();
    if (!n || !p) {
      toast(t.enterNamePhone);
      return;
    }
    link.mutate(
      { sessionId: session.id, body: { name: n, phone: p } },
      {
        onSuccess: () => {
          toast(t.guestLinked);
          f7.sheet.close();
        },
      },
    );
  };

  const removeLink = () =>
    unlink.mutate(
      { sessionId: session.id },
      {
        onSuccess: () => {
          toast(t.guestUnlinked);
          f7.sheet.close();
        },
      },
    );

  return (
    <PageContent>
      {linked && (
        <>
          <BlockTitle>{t.currentGuest}</BlockTitle>
          <List strongIos insetIos dividersIos>
            <ListItem
              link="#"
              title={linked.name}
              after={linked.phone ? formatPhone(linked.phone) : undefined}
              footer={t.visitsFooter(linked.totalVisits)}
              onClick={() => f7.views.current.router.navigate(`/customers/${linked.id}`)}
            >
              <i slot="media" className="f7-icons" style={{ fontSize: 20 }}>
                person_crop_circle
              </i>
            </ListItem>
            <ListItem
              link="#"
              title={t.unlinkGuest}
              onClick={removeLink}
              style={{ color: '#ff3b30' }}
            >
              <i slot="media" className="f7-icons" style={{ fontSize: 20, color: '#ff3b30' }}>
                person_crop_circle_badge_xmark
              </i>
            </ListItem>
          </List>
        </>
      )}

      <BlockTitle>{linked ? t.linkDifferent : t.findAddGuest}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListInput
          label={t.nameLabel}
          type="text"
          placeholder={t.guestNamePlaceholder}
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          clearButton
        />
        <ListInput
          label={t.phoneLabel}
          type="tel"
          placeholder={t.phoneNumberPlaceholder}
          value={phone}
          onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
          clearButton
        />
      </List>

      {showSuggestions && (
        <>
          <BlockTitle>{t.existingGuests}</BlockTitle>
          <List strongIos insetIos dividersIos>
            {suggestions.map((c) => (
              <ListItem
                key={c.id}
                link="#"
                title={`${c.firstName} ${c.lastName}`.trim()}
                after={formatPhone(primaryPhone(c)) || undefined}
                footer={c.email ?? undefined}
                onClick={() => linkExisting(c)}
              />
            ))}
          </List>
        </>
      )}

      <Block>
        <Button fill large round onClick={linkNew} disabled={!name.trim() || !phone.trim()}>
          {linked ? t.linkNewGuest : t.linkGuest}
        </Button>
      </Block>
    </PageContent>
  );
}
